<template>
  <el-card>
    <!-- Start loader -->
    <div v-if="isLoading">
      <h5 class="text-center">
        <i class="fa fa-spinner fa-spin"></i>
        <span>Loading...</span>
      </h5>
    </div>
    <!-- End loader -->

    <div v-else>
      <el-form :name="`${integrationName}-ava-entity-mapping`"
               class="text-center"
               @submit.native.prevent>

        <!-- Start Header -->
        <div class="d-flex justify-content-between">
          <h5>
            Contact Properties
          </h5>
        </div>
        <!-- End Header -->

        <!-- Start Column Labels -->
        <div v-if="mappings.length > 0" class="row form-group mt-3">
          <div class="col-md-4 mb-1 text-left">
            <small class="font-weight-bold">AVA Entity</small>
          </div>
          <div class="col-md-2 mb-1 text-center">
            <small class="font-weight-bold">Direction</small>
          </div>
          <div class="col-md-4 mb-1 text-left">
            <small class="font-weight-bold">{{ integrationNameWithCasing }} Contact Property</small>
          </div>
        </div>
        <!-- End Column Labels -->

        <!-- Start Mapping Rows -->
        <div v-for="(mapping, index) in mappings"
             :key="`${index}-ava-mapping`">
          <div class="row form-group mt-3">

            <!-- Start AVA Entity Select -->
            <div class="col-md-4">
              <el-form-item class="mb-1">
                <el-select v-model="mapping.aloware_key"
                           class="w-full"
                           filterable
                           placeholder="Select AVA Entity"
                           @change="handleAvaEntitySelection(mapping)">
                  <el-option
                      v-for="option in avaEntityOptions"
                      :key="option.key"
                      :label="option.label"
                      :value="option.key"
                      :disabled="isSelectedAvaEntity(option.key, mapping.aloware_key)">
                    <i v-if="isSelectedAvaEntity(option.key, mapping.aloware_key)"
                       class="fa fa-ban text-grey-5">
                    </i>
                    {{ option.label }}
                  </el-option>
                </el-select>
              </el-form-item>
            </div>
            <!-- End AVA Entity Select -->

            <!-- Start Direction Selector -->
            <div class="col-md-2 text-center">
              <direction-selector :initial-state="mapping.direction || IntegrationPropertyMapDirections.DIRECTION_OUTBOUND"
                                  :allowed-states="[IntegrationPropertyMapDirections.DIRECTION_OUTBOUND]"
                                  :disabled="true">
              </direction-selector>
            </div>
            <!-- End Direction Selector -->

            <!-- Start Integration Property Select -->
            <div class="col-md-4">
              <el-form-item class="mb-1">
                <el-select v-model="mapping.external_key"
                           class="w-full"
                           filterable
                           placeholder="Select Contact Property"
                           @change="handleIntegrationPropertySelection(mapping)">
                  <el-option-group
                      v-for="group in groupedIntegrationProperties"
                      :key="group.label"
                      :label="group.label">
                    <el-option
                        v-for="option in group.data"
                        :key="option.name"
                        :label="option.label"
                        :value="option.name"
                        :disabled="!option.is_mappable || isSelectedIntegrationProperty(option.name, mapping.external_key) || option.mapped_in_contact_mapper"
                        :title="option.mapped_in_contact_mapper ? 'Already mapped in Contact Property Mapper. Please unmap and save first.' : ''">
                      <i v-if="isSelectedIntegrationProperty(option.name, mapping.external_key)"
                         class="fa fa-ban text-grey-5">
                      </i>
                      {{ formatCrmOptionLabel(option) }}
                    </el-option>
                  </el-option-group>
                </el-select>
              </el-form-item>
            </div>
            <!-- End Integration Property Select -->

            <!-- Start Delete button -->
            <div class="col-md-2 text-center">
              <el-button size="small"
                         circle
                         plain
                         :disabled="mappings.length === 1"
                         @click="removeMapping(index)">
                <i class="fa fa-trash"></i>
              </el-button>
            </div>
            <!-- End Delete Button -->
          </div>
        </div>
        <!-- End Mapping Rows -->

        <!-- Start Add mapping button -->
        <div class="row form-group">
          <div class="col-md-10 text-center">
            <el-button size="small"
                       @click="addMapping">
              <i class="fa fa-plus-circle"></i>
              <span class="ml-1 font-weight-bold">ADD MAPPING</span>
            </el-button>
          </div>
        </div>
        <!-- End Add mapping button -->

      </el-form>
    </div>
  </el-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import DirectionSelector from '../direction-selector.vue'
import * as IntegrationPropertyMapDirections from '../../../constants/integration-property-map-directions'

export default {
  components: {
    DirectionSelector,
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    integrationName: {
      type: String,
      required: true
    },
    integrationNameWithCasing: {
      type: String,
      required: true
    },
    staticName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      mappings: [],
      IntegrationPropertyMapDirections
    }
  },
  computed: {
    ...mapGetters('avaEntityMapping', [
      'getAvaEntities',
      'getIntegrationContactProperties',
      'getAvaEntityContactMappings'
    ]),
    avaEntityOptions() {
      return this.getAvaEntities
    },
    integrationContactPropertyOptions() {
      return this.getIntegrationContactProperties
    },
    groupedIntegrationProperties() {
      const grouped = []

      for (const prop of this.integrationContactPropertyOptions) {
        const groupLabel = prop.group_label || 'Other'
        let existingGroup = grouped.find(g => g.label === groupLabel)

        if (!existingGroup) {
          existingGroup = { label: groupLabel, data: [] }
          grouped.push(existingGroup)
        }

        existingGroup.data.push(prop)
      }

      return grouped
    }
  },
  methods: {
    ...mapActions('avaEntityMapping', [
      'fetchAvaEntityContactMappings',
      'storeAvaEntityContactMappings'
    ]),
    async fetchAvaEntityOptions() {
      try {
        await this.$store.dispatch('avaEntityMapping/getAvaEntities')
      } catch (e) {
        console.error('Failed to fetch AVA entity options:', e)
      }
    },
    async fetchIntegrationContactPropertyOptions() {
      try {
        await this.$store.dispatch('avaEntityMapping/getIntegrationContactProperties', this.integrationName)
      } catch (e) {
        console.error('Failed to fetch integration contact property options:', e)
      }
    },
    async fetchContactPropertyMappings() {
      try {
        this.mappings = await this.fetchAvaEntityContactMappings(this.integrationName)

        // If no mappings exist, add one empty mapping to start
        if (this.mappings.length === 0) {
          this.addMapping()
        }
      } catch (e) {
        console.error('Failed to fetch contact property mappings:', e)
      }
    },
    addMapping() {
      this.mappings.push({
        aloware_key: '',
        external_key: '',
        direction: IntegrationPropertyMapDirections.DIRECTION_OUTBOUND
      })
    },
    removeMapping(index) {
      this.mappings.splice(index, 1)
    },
    isSelectedAvaEntity(key, currentKey) {
      // Don't disable if it's the current selection
      if (key === currentKey) {
        return false
      }
      // Disable if already selected in another mapping
      return this.mappings.some(m => m.aloware_key === key)
    },
    isSelectedIntegrationProperty(name, currentName) {
      // Don't disable if it's the current selection
      if (name === currentName) {
        return false
      }
      // Disable if already selected in another mapping
      return this.mappings.some(m => m.external_key === name)
    },
    handleAvaEntitySelection(mapping) {
      // AVA entities are always outbound, no conflict check needed, just ensure direction is set
      if (!mapping.direction) {
        mapping.direction = IntegrationPropertyMapDirections.DIRECTION_OUTBOUND
      }
    },
    formatFieldType(option) {
      if (option.type && option.type !== option.field_type) {
        return `${option.field_type}/${option.type}`
      }
      return option.field_type
    },
    formatCrmOptionLabel(option) {
      const type = this.formatFieldType(option)
      const readOnly = option.direction === IntegrationPropertyMapDirections.DIRECTION_INBOUND
      return readOnly ? `${option.label} (${type}, read-only)` : `${option.label} (${type})`
    },
    handleIntegrationPropertySelection(mapping) {
      // Get the selected integration property
      const selectedProperty = this.integrationContactPropertyOptions.find(
        option => option.name === mapping.external_key
      )

      if (!selectedProperty) {
        return
      }

      // Check for direction conflict
      // AVA entities are OUTBOUND-ONLY, if the HubSpot property is INBOUND-ONLY (read-only), there's a conflict
      if (selectedProperty.direction === IntegrationPropertyMapDirections.DIRECTION_INBOUND) {
        // Clear the selection
        mapping.external_key = ''

        // Notify the user
        this.$notify({
          title: 'AVA Entity Mapping',
          message: 'Direction conflict detected. Read-only properties cannot be mapped to outbound AVA entities. Please select a different property.',
          type: 'error',
        })
      }
    }
  },
  async mounted() {
    this.isLoading = true
    try {
      await Promise.all([
        this.fetchAvaEntityOptions(),
        this.fetchIntegrationContactPropertyOptions(),
        this.fetchContactPropertyMappings()
      ])
    } catch (e) {
      this.$notify({
        title: 'Error',
        message: 'Failed to load AVA entity or Integration Properties.',
        type: 'error',
      })
    } finally {
      this.isLoading = false
    }
  }
}
</script>

<style scoped>
.w-full {
  width: 100%;
}

.font-weight-bold {
  font-weight: bold;
}

.text-grey-5 {
  color: #909399;
}

.mt-3 {
  margin-top: 1rem;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.ml-1 {
  margin-left: 0.25rem;
}

.text-center {
  text-align: center;
}

.d-flex {
  display: flex;
}

.justify-content-between {
  justify-content: space-between;
}
</style>

